import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, Check } from "lucide-react";
import { CALC_TASKS } from "../data/content";
import { submitCalculatorLead } from "../lib/api";

const OFFLOAD = 0.75; // share of task time your right hand takes over

export default function Calculator() {
  const [hours, setHours] = useState(Object.fromEntries(CALC_TASKS.map((t) => [t.key, t.defaultHours])));
  const [rate, setRate] = useState(75);
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const { weeklySaved, monthlyHours, monthlyMoney, totalWeekly } = useMemo(() => {
    const totalWeekly = Object.values(hours).reduce((a, b) => a + Number(b || 0), 0);
    const weeklySaved = Math.round(totalWeekly * OFFLOAD);
    const monthlyHours = Math.round(weeklySaved * 4.33);
    const monthlyMoney = Math.round(monthlyHours * Number(rate || 0));
    return { weeklySaved, monthlyHours, monthlyMoney, totalWeekly };
  }, [hours, rate]);

  const setTask = (key, val) => setHours((h) => ({ ...h, [key]: val }));

  const handleSend = async (e) => {
    e.preventDefault();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Please enter a valid email.");
      return;
    }
    setSending(true);
    try {
      await submitCalculatorLead({
        email,
        weekly_hours_saved: weeklySaved,
        monthly_hours: monthlyHours,
        monthly_money: monthlyMoney,
        hourly_rate: rate,
      });
      setSent(true);
      toast.success("Sent. Check your inbox for your estimate.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-12 gap-10 lg:gap-16" data-testid="roi-calculator">
      {/* Inputs */}
      <div className="lg:col-span-6 space-y-8">
        {CALC_TASKS.map((t) => (
          <div key={t.key} data-testid={`calc-task-${t.key}`}>
            <div className="flex items-baseline justify-between mb-3">
              <label className="text-sm md:text-base text-neutral-700">{t.label}</label>
              <span className="font-display text-lg tabular-nums">{hours[t.key]}<span className="text-neutral-400 text-sm ml-1">hrs/wk</span></span>
            </div>
            <input
              type="range" min="0" max="30" value={hours[t.key]}
              onChange={(e) => setTask(t.key, Number(e.target.value))}
              className="w-full accent-black cursor-pointer"
              data-testid={`calc-slider-${t.key}`}
            />
          </div>
        ))}
        <div className="pt-4 border-t border-black/10">
          <div className="flex items-baseline justify-between mb-3">
            <label className="text-sm md:text-base text-neutral-700">Your hourly value</label>
            <span className="font-display text-lg tabular-nums">${rate}<span className="text-neutral-400 text-sm ml-1">/hr</span></span>
          </div>
          <input
            type="range" min="25" max="500" step="5" value={rate}
            onChange={(e) => setRate(Number(e.target.value))}
            className="w-full accent-black cursor-pointer"
            data-testid="calc-slider-rate"
          />
        </div>
      </div>

      {/* Result */}
      <div className="lg:col-span-6">
        <div className="rounded-3xl bg-[#0a0a0a] text-white p-10 md:p-12 h-full flex flex-col justify-center">
          <p className="text-sm text-neutral-500 mb-8">Your right hand gives back</p>
          <div className="space-y-8">
            <div>
              <motion.div key={weeklySaved} initial={{ opacity: 0.4, y: 6 }} animate={{ opacity: 1, y: 0 }} className="font-display text-6xl md:text-7xl tracking-tighter font-medium tabular-nums" data-testid="calc-weekly">
                {weeklySaved}<span className="text-neutral-500 text-3xl md:text-4xl ml-2">hrs/week</span>
              </motion.div>
              <p className="mt-2 text-neutral-400">reclaimed from {totalWeekly} hrs of busywork</p>
            </div>
            <div className="grid grid-cols-2 gap-6 pt-8 border-t border-white/10">
              <div>
                <motion.div key={monthlyHours} initial={{ opacity: 0.4 }} animate={{ opacity: 1 }} className="font-display text-4xl tracking-tighter font-medium tabular-nums" data-testid="calc-monthly-hours">{monthlyHours}</motion.div>
                <p className="mt-1 text-sm text-neutral-500">hours back per month</p>
              </div>
              <div>
                <motion.div key={monthlyMoney} initial={{ opacity: 0.4 }} animate={{ opacity: 1 }} className="font-display text-4xl tracking-tighter font-medium tabular-nums" data-testid="calc-monthly-money">${monthlyMoney.toLocaleString()}</motion.div>
                <p className="mt-1 text-sm text-neutral-500">value of your time, monthly</p>
              </div>
            </div>
          </div>
          <div className="mt-10 pt-8 border-t border-white/10">
            {sent ? (
              <div className="flex items-center gap-3 text-neutral-300" data-testid="calc-lead-success">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white/10"><Check size={16} /></span>
                <p className="text-sm">Your estimate is on its way. Talk soon.</p>
              </div>
            ) : (
              <form onSubmit={handleSend} data-testid="calc-lead-form">
                <label className="text-sm text-neutral-400">Email me this estimate</label>
                <div className="mt-3 flex flex-col sm:flex-row gap-3">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@brokerage.com"
                    className="flex-1 bg-transparent border border-white/20 rounded-full px-5 py-3 text-white placeholder:text-neutral-600 focus:border-white/60 focus:outline-none transition-colors"
                    data-testid="calc-lead-email"
                  />
                  <button
                    type="submit"
                    disabled={sending}
                    className="group inline-flex items-center justify-center gap-2 rounded-full bg-white text-black px-6 py-3 transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0"
                    data-testid="calc-lead-submit"
                  >
                    {sending ? "Sending…" : <>Send it <ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-0.5" /></>}
                  </button>
                </div>
              </form>
            )}
          </div>
          <p className="mt-6 text-xs text-neutral-600">Estimate assumes your right hand absorbs ~{Math.round(OFFLOAD * 100)}% of these tasks. Your actual mileage is usually higher.</p>
        </div>
      </div>
    </div>
  );
}
